import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Check, Tag as TagIcon } from 'lucide-react';

/**
 * Выбор меток: поиск и список, из которого набирают нужные.
 *
 * <p>Не набор кнопок, как у ролей: ролей три-четыре и они известны заранее, а метка
 * заводится на каждый сервис - через год их будут десятки, и разложить такой список
 * кнопками по странице уже нельзя.
 *
 * @param tags     все доступные метки
 * @param selected идентификаторы выбранных
 */
const TagPicker = ({ tags, selected, onChange, placeholder = 'Найти метку…', emptyHint }) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const byId = useMemo(() => new Map(tags.map(tag => [tag.id, tag])), [tags]);

    const found = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return tags;
        return tags.filter(tag =>
            tag.name.toLowerCase().includes(needle)
            || (tag.description || '').toLowerCase().includes(needle));
    }, [tags, query]);

    const toggle = (id) => {
        onChange(selected.includes(id) ? selected.filter(item => item !== id) : [...selected, id]);
    };

    return (
        <div ref={containerRef} className="relative">
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                    {selected.map(id => {
                        const tag = byId.get(id);
                        return (
                            <span
                                key={id}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium bg-blue-600 text-white"
                            >
                                <TagIcon size={13} />
                                {tag ? tag.name : `метка ${id}`}
                                <button
                                    type="button"
                                    onClick={() => toggle(id)}
                                    aria-label="Убрать метку"
                                    className="bg-transparent border-none text-white/80 hover:text-white cursor-pointer p-0 flex"
                                >
                                    <X size={14} />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}

            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                    className="w-full pl-9 pr-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10"
                    value={query}
                    placeholder={placeholder}
                    onChange={e => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                />
            </div>

            {open && (
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                    {found.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-500 m-0">
                            {tags.length === 0
                                ? (emptyHint || 'Меток пока нет.')
                                : 'Ничего не найдено.'}
                        </p>
                    ) : found.map(tag => {
                        const isSelected = selected.includes(tag.id);
                        return (
                            <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggle(tag.id)}
                                className={`w-full flex items-start gap-3 px-4 py-2.5 text-left cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                                    isSelected ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                            >
                                <span className="flex flex-col overflow-hidden flex-1">
                                    <span className="text-sm font-medium text-gray-900 truncate">{tag.name}</span>
                                    {tag.description && (
                                        <span className="text-xs text-gray-500 truncate">{tag.description}</span>
                                    )}
                                    {typeof tag.userCount === 'number' && (
                                        <span className="text-xs text-gray-400">{tag.userCount} чел.</span>
                                    )}
                                </span>
                                {isSelected && <Check size={16} className="text-blue-600 shrink-0 mt-0.5" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TagPicker;
